/**
 * Zoekvraag voor Nieuws & sentiment.
 *
 * De gebruiker kiest per term hoe hij meetelt (OF / EN / NIET). Coosto en Google Nieuws verstaan
 * dezelfde schrijfwijze: `(a OR b) AND c -d`. Let op: `NOT` werkt bij geen van beide (Google leest
 * het als woord, Coosto geeft nul resultaten); uitsluiten gaat met een minteken.
 */

export type Operator = 'OF' | 'EN' | 'NIET';
export interface Zoekterm { tekst: string; op: Operator }

export const OPERATORS: { op: Operator; uitleg: string }[] = [
  { op: 'OF', uitleg: 'één van deze termen' },
  { op: 'EN', uitleg: 'moet er ook in staan' },
  { op: 'NIET', uitleg: 'uitsluiten' },
];

export const PERIODES: { dagen: number; label: string }[] = [
  { dagen: 7, label: 'Afgelopen week' },
  { dagen: 30, label: 'Afgelopen maand' },
  { dagen: 90, label: 'Afgelopen 3 maanden' },
  { dagen: 180, label: 'Afgelopen half jaar' },
  { dagen: 365, label: 'Afgelopen jaar' },
];
export const STANDAARD_PERIODE = 90;

const VOLGORDE: Operator[] = ['OF', 'EN', 'NIET'];
/** Vaste volgorde (OF, dan EN, dan NIET), zodat de rij termen leest zoals de zoekvraag werkt. */
export const sorteer = (termen: Zoekterm[]): Zoekterm[] =>
  VOLGORDE.flatMap((op) => termen.filter((t) => t.op === op));

export const schoon = (t: string): string => String(t || '').trim().replace(/^"+|"+$/g, '').trim();
/** Een woordgroep gaat tussen aanhalingstekens, zodat hij als geheel wordt gezocht. */
const deel = (t: string): string => { const s = schoon(t); return /\s/.test(s) ? `"${s}"` : s; };

function bouw(termen: Zoekterm[], of: string, en: string, niet: (d: string) => string): string {
  const groep = (op: Operator) => termen.filter((t) => t.op === op).map((t) => deel(t.tekst)).filter(Boolean);
  const ofs = groep('OF'); const ens = groep('EN'); const nieten = groep('NIET');
  let s = ofs.join(of);
  if (ofs.length > 1 && (ens.length || nieten.length)) s = `(${s})`;
  ens.forEach((e) => { s = s ? `${s}${en}${e}` : e; });
  if (!s) return ''; // alleen uitsluiten is geen zoekvraag
  nieten.forEach((n) => { s = `${s} ${niet(n)}`; });
  return s;
}

/** Zoekvraag zoals die naar Coosto en Google Nieuws gaat. */
export const bouwZoekvraag = (termen: Zoekterm[]): string => bouw(termen, ' OR ', ' AND ', (d) => `-${d}`);
/** Dezelfde zoekvraag, leesbaar in het Nederlands. */
export const leesbareZoekvraag = (termen: Zoekterm[]): string => bouw(termen, ' OF ', ' EN ', (d) => `NIET ${d}`);

/** Zelf geschreven zoekvraag (OF/EN/NIET of OR/AND/NOT, in hoofdletters) → schrijfwijze van de zoekmachines. */
export function naarZoekmachine(tekst: string): string {
  // Wat tussen aanhalingstekens staat blijft ongemoeid ("wel OF niet" is een woordgroep).
  const s = String(tekst || '').replace(/"[^"]*"|\b(OF|OR|EN|AND)\b|\b(?:NIET|NOT)\s+/g, (m, op) => {
    if (m.startsWith('"')) return m;
    if (op) return op === 'OF' || op === 'OR' ? 'OR' : 'AND';
    return '-';
  });
  // "a AND -b" is hetzelfde als "a -b"; zo blijft het voor beide zoekmachines gelijk.
  return s.replace(/\s+AND\s+-/g, ' -').replace(/\s+/g, ' ').trim();
}

/** Schrijfwijze van de zoekmachines → Nederlandse weergave. */
export function naarNederlands(q: string): string {
  return String(q || '').replace(/"[^"]*"|\b(OR|AND)\b|(^|[\s(])-(?=\S)/g, (m, op, voor) => {
    if (m.startsWith('"')) return m;
    if (op) return op === 'OR' ? 'OF' : 'EN';
    return `${voor}NIET `;
  });
}

/**
 * Een opgeslagen zoekvraag terug naar termen met hun operator. null = niet als eenvoudige lijst te
 * lezen (losse woorden zonder operator, geneste haakjes, OF na EN) → die blijft een vrije zoekvraag.
 */
export function leesTermen(q: string): Zoekterm[] | null {
  const delen = String(q || '').match(/-?"[^"]*"|[()]|[^\s()]+/g) || [];
  const uit: Zoekterm[] = [];
  let op: Operator | null = 'OF';   // operator voor de volgende term; null = er moet eerst een operator komen
  let diepte = 0; let enGezien = false;
  for (const t of delen) {
    if (t === '(') { diepte += 1; if (diepte > 1 || uit.length > 0) return null; continue; }
    if (t === ')') { diepte -= 1; continue; }
    if (t === 'OR') { if (enGezien) return null; op = 'OF'; continue; }
    if (t === 'AND') { if (diepte > 0) return null; enGezien = true; op = 'EN'; continue; }
    if (t.startsWith('-') && t.length > 1) { if (diepte > 0) return null; uit.push({ tekst: schoon(t.slice(1)), op: 'NIET' }); op = null; continue; }
    if (op === null) return null; // twee termen zonder operator ertussen = losse woorden (EN), niet eenduidig
    uit.push({ tekst: schoon(t), op });
    op = null;
  }
  return diepte === 0 ? uit.filter((t) => t.tekst) : null;
}
